"use client";

import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface StarterPackItem {
  text: string;
  icon?: React.ElementType;
}

export interface StarterPackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClaim: () => void;
  coverImage: string;
  title?: string;
  subtitle?: string;
  perks?: string[];
  features?: string[];
  /** Badge label on the cover banner — e.g. "GRATUIT" or "149 AP" */
  badgeLabel?: string;
  /** Replace the default badge with an image (e.g. apoints icon) */
  badgeImage?: string;
  claimLabel?: string;
  cancelLabel?: string;
}

// ─── Variants ──────────────────────────────────────────────────────────────────

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.18, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: "easeIn" } },
} as const;

const panelVariants = {
  hidden: { opacity: 0, scale: 0.94, y: 28 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 520,
      damping: 28,
      mass: 0.7,
      staggerChildren: 0.05,
      delayChildren: 0.04,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    y: 10,
    transition: { duration: 0.13, ease: [0.4, 0, 1, 1] },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 440, damping: 22 },
  },
} as const;

const perkVariants: any = {
  hidden: { opacity: 0, x: -10 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 380,
      damping: 22,
      delay: i * 0.045,
    },
  }),
};

const claimHover = {
  scale: 1.03,
  boxShadow:
    "0 0 0 1.5px rgba(78,155,255,0.55), 0 0 24px rgba(23,100,200,0.55), inset 0 1px 0 rgba(255,255,255,0.12)",
  transition: { type: "spring", stiffness: 460, damping: 16 },
} as const;

const claimTap = {
  scale: 0.96,
  transition: { type: "spring", stiffness: 600, damping: 20 },
} as const;
const cancelHover = {
  scale: 1.03,
  boxShadow:
    "0 0 0 1.5px rgba(220,80,90,0.55), 0 0 24px rgba(107,42,50,0.7), inset 0 1px 0 rgba(255,255,255,0.08)",
  transition: { type: "spring", stiffness: 460, damping: 16 },
} as const;
const cancelTap = {
  scale: 0.96,
  transition: { type: "spring", stiffness: 600, damping: 20 },
} as const;

// ─── Portal ─────────────────────────────────────────────────────────────────────

const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const elRef = useRef<HTMLDivElement | null>(null);
  if (!elRef.current) elRef.current = document.createElement("div");
  useEffect(() => {
    const el = elRef.current!;
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);
  return createPortal(children, elRef.current);
};

// ─── Component ─────────────────────────────────────────────────────────────────

export default function StarterPackModal({
  isOpen,
  onClose,
  onClaim,
  coverImage,
  title = "Pachet Incepator",
  subtitle = "Tot ce ai nevoie pentru a te lansa la drum",
  perks = [],
  features = [],
  badgeLabel = "GRATUIT",
  badgeImage,
  claimLabel = "REVENDICA",
  cancelLabel = "ANULEAZA",
}: StarterPackModalProps) {
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleClaim = useCallback(() => {
    onClaim();
    onClose();
  }, [onClaim, onClose]);

  const content = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="starter-pack-overlay"
          variants={prefersReduced ? undefined : overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 flex items-center justify-center p-6"
          style={{
            background: "rgba(0,0,0,0.7)",
            zIndex: 5000000,
          }}
          onClick={onClose}
        >
          <motion.div
            key="starter-pack-panel"
            variants={prefersReduced ? undefined : panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[460px] rounded-2xl overflow-hidden"
            style={{
              boxShadow:
                "0 0 0 1px rgba(255,255,255,0.07), 0 24px 60px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.4)",
            }}
          >
            {/* ── Compact cover strip ── */}
            <BackgroundImage
              imageSrc={coverImage}
              imageClassName="!object-cover !rounded-none"
              className="h-[145px] relative"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-black/80" />

              {/* Close button */}
              <motion.button
                onClick={onClose}
                variants={prefersReduced ? undefined : itemVariants}
                whileHover={{
                  scale: 1.12,
                  rotate: 90,
                  transition: { type: "spring", stiffness: 400, damping: 18 },
                }}
                whileTap={{ scale: 0.88 }}
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M1 1l8 8M9 1L1 9"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.button>

              {/* Badge pill */}
              <motion.div
                variants={prefersReduced ? undefined : itemVariants}
                className="absolute top-3 left-3 flex items-center gap-1 rounded-lg px-2.5 py-1"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 100%)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  boxShadow:
                    "0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
              >
                {badgeImage && (
                  <img
                    src={badgeImage}
                    width={18}
                    height={18}
                    draggable={false}
                  />
                )}
                <span className="gilroy-font !font-[600] text-white text-[0.9rem] leading-none">
                  {badgeLabel}
                </span>
              </motion.div>

              {/* Name at bottom */}
              <div className="absolute bottom-0 left-0 right-0 px-5 pb-3">
                <motion.h2
                  variants={prefersReduced ? undefined : itemVariants}
                  className="bebas-neue-font text-white text-[2.2rem] leading-[1]"
                  style={{ textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}
                >
                  {title}
                </motion.h2>
                {subtitle && (
                  <motion.p
                    variants={prefersReduced ? undefined : itemVariants}
                    className="chakra-petch-font text-white/55 text-[0.72rem] mt-0.5"
                  >
                    {subtitle}
                  </motion.p>
                )}
              </div>
            </BackgroundImage>

            {/* ── Body ── */}
            <div className="bg-[#0d1117] px-5 py-4 flex flex-col gap-3.5">
              {/* Perks */}
              {perks.length > 0 && (
                <motion.div
                  variants={prefersReduced ? undefined : itemVariants}
                  className="flex flex-col gap-1.5"
                >
                  <p className="chakra-petch-font text-white/30 text-[0.62rem] uppercase tracking-[0.14em]">
                    Ce primesti
                  </p>
                  <div className="flex flex-col gap-1">
                    {perks.map((perk, i) => (
                      <motion.div
                        key={i}
                        custom={i}
                        variants={prefersReduced ? undefined : perkVariants}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{
                            background:
                              "linear-gradient(135deg, #4e9bff, #1785ef)",
                            boxShadow: "0 0 5px rgba(23,133,239,0.5)",
                          }}
                        />
                        <span className="gilroy-font !font-[500] text-white/75 text-[0.83rem]">
                          {perk}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Feature badges */}
              {features.length > 0 && (
                <motion.div
                  variants={prefersReduced ? undefined : itemVariants}
                  className="flex flex-wrap gap-1.5"
                >
                  {features.map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1 rounded-md px-2.5 py-0.5"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <span className="gilroy-font !font-[500] text-white/55 text-[0.75rem]">
                        {feature}
                      </span>
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Cost row */}
              <motion.div
                variants={prefersReduced ? undefined : itemVariants}
                className="flex items-center justify-between rounded-lg px-3.5 py-2.5"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <div className="flex flex-col">
                  <span className="chakra-petch-font text-white/30 text-[0.6rem] uppercase tracking-widest">
                    Pret
                  </span>
                  <span
                    className="gilroy-font !font-[700] text-[1.15rem] leading-tight"
                    style={{
                      color: "#4e9bff",
                      textShadow: "0 0 16px rgba(23,133,239,0.4)",
                    }}
                  >
                    {badgeLabel}
                  </span>
                </div>
                {badgeImage ? (
                  <img
                    src={badgeImage}
                    width={26}
                    height={26}
                    draggable={false}
                  />
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z"
                      fill="#4e9bff"
                      style={{
                        filter: "drop-shadow(0 0 6px rgba(23,133,239,0.6))",
                      }}
                    />
                  </svg>
                )}
              </motion.div>

              {/* Divider */}
              <div className="h-[1px] bg-gradient-to-r from-white/0 via-white/7 to-white/0" />

              {/* Actions */}
              <motion.div
                variants={prefersReduced ? undefined : itemVariants}
                className="grid grid-cols-2 gap-2.5 bebas-neue-font"
              >
                {/* Claim */}
                <motion.button
                  onClick={handleClaim}
                  whileHover={prefersReduced ? undefined : claimHover}
                  whileTap={prefersReduced ? undefined : claimTap}
                  className="relative overflow-hidden rounded-lg text-white text-[1.2rem] py-2.5 px-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #1D3650 0%, #1e4974 50%, #1D3650 100%)",
                  }}
                >
                  <div
                    className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none rounded-lg"
                    style={{
                      background:
                        "radial-gradient(60.63% 70.9% at 50% 45%, rgba(78,155,255,1) 0%, rgba(23,133,239,0.4) 65%, rgba(23,133,239,0) 100%)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <motion.span
                    className="pointer-events-none absolute inset-0"
                    initial={{ x: "-110%", skewX: "-18deg", opacity: 0 }}
                    whileHover={{
                      x: "110%",
                      opacity: [0, 0.22, 0],
                      transition: { duration: 0.4, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0 }}
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(120,190,255,0.35), transparent)",
                      width: "60%",
                    }}
                  />
                  <span style={{ color: "#4e9bff" }}>{claimLabel}</span>
                </motion.button>

                {/* Cancel */}
                <motion.button
                  onClick={onClose}
                  whileHover={prefersReduced ? undefined : cancelHover}
                  whileTap={prefersReduced ? undefined : cancelTap}
                  className="relative overflow-hidden rounded-lg text-white text-[1.2rem] py-2.5 px-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #3d272f 0%, #6b2a32 50%, #3d272f 100%)",
                  }}
                >
                  <div
                    className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none rounded-lg"
                    style={{
                      background:
                        "radial-gradient(60.63% 70.9% at 50% 45%, rgba(255,39,39,1) 0%, rgba(255,39,39,0.4) 65%, rgba(255,39,39,0) 100%)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <motion.span
                    className="pointer-events-none absolute inset-0"
                    initial={{ x: "-110%", skewX: "-18deg", opacity: 0 }}
                    whileHover={{
                      x: "110%",
                      opacity: [0, 0.14, 0],
                      transition: { duration: 0.4, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0 }}
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,200,200,0.25), transparent)",
                      width: "60%",
                    }}
                  />
                  {cancelLabel}
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof window === "undefined") return null;
  return <ModalPortal>{content}</ModalPortal>;
}
